// Shared helpers for alternate plans (internships / off-site classes).
// A plan is either recurring "weekly" (matches weekday names in `days`) or
// "dates" (matches explicit ISO dates in `dates`). `skip_dates` always
// suppresses a single occurrence.

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export function weekdayKeyOf(dateIso) {
  const d = new Date(dateIso + "T00:00:00");
  // JS getDay(): 0=Sun..6=Sat -> shift so Monday=0
  return WEEKDAYS[(d.getDay() + 6) % 7];
}

/** Whether an alternate plan applies on a specific ISO date (YYYY-MM-DD). */
export function planActiveOnDate(plan, dateIso) {
  if (!plan || !dateIso) return false;
  if ((plan.skip_dates || []).includes(dateIso)) return false;
  if (plan.plan_type === "dates") return (plan.dates || []).includes(dateIso);
  if (plan.plan_type === "range") {
    const s = plan.start_date || "";
    const e = plan.end_date || "";
    return !!s && !!e && s <= dateIso && dateIso <= e;
  }
  // weekly / legacy
  const wd = weekdayKeyOf(dateIso);
  return (plan.days || []).map((x) => x.toLowerCase()).includes(wd);
}

/** Human label for a plan's schedule (weekday list, list of dates, or a date range). */
export function describePlanSchedule(plan) {
  if (!plan) return "";
  const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (plan.plan_type === "range") {
    if (!plan.start_date || !plan.end_date) return "No dates";
    return `${fmt(plan.start_date)} – ${fmt(plan.end_date)}`;
  }
  if (plan.plan_type === "dates") {
    const ds = plan.dates || [];
    if (ds.length === 0) return "No dates";
    return ds.map(fmt).join(", ");
  }
  return (plan.days || []).map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(", ");
}

/** Format an "HH:MM" 24h string as a friendly 12h time (e.g. "13:30" -> "1:30 PM"). */
export function fmtTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

/** Whether a student with this arrival/departure window attends ANY part of a block. */
export function presentInBlock(arrival, departure, block) {
  const bs = block.start_time || "";
  const be = block.end_time || "";
  if (arrival && be && be <= arrival) return false; // block ends before arrival
  if (departure && bs && bs >= departure) return false; // block starts after departure
  return true;
}

/** Names of full-day blocks the student misses entirely. blocks = [{name,start_time,end_time}]. */
export function computeMissedBlocks(arrival, departure, blocks) {
  return (blocks || [])
    .filter((b) => b.start_time && b.end_time && !presentInBlock(arrival, departure, b))
    .map((b) => b.name);
}

/** One-line summary of a plan's on-campus time window (falls back to server-computed values). */
export function describePlanTimes(plan) {
  if (!plan) return "";
  const a = plan.arrival_time || plan.effective_arrival || "";
  const d = plan.departure_time || plan.effective_departure || "";
  if (a && d) return `On campus ${fmtTime(a)}–${fmtTime(d)}`;
  if (d) return `Leaves at ${fmtTime(d)}`;
  if (a) return `Arrives at ${fmtTime(a)}`;
  return "";
}

/** Compact window label for dense views (grid cells): "→2:30p", "10:00a→", or "10:00a–2:30p". */
export function shortPlanWindow(plan) {
  if (!plan) return "";
  const short = (hhmm) => fmtTime(hhmm).replace(":00", "").replace(" AM", "a").replace(" PM", "p");
  const a = plan.arrival_time || plan.effective_arrival || "";
  const d = plan.departure_time || plan.effective_departure || "";
  if (a && d) return `${short(a)}–${short(d)}`;
  if (d) return `→${short(d)}`;
  if (a) return `${short(a)}→`;
  return "";
}

const STD_START = "08:30";
const STD_FULL_END = "14:00";

/**
 * A short on-campus note for a student's per-weekday schedule when it deviates
 * from the standard full day (08:30–14:00): "Arrives 9:30a", "Leaves 12p", or
 * "9:30a–12p". Returns "" when the student is on a standard full day, and
 * "Not scheduled" when the day is explicitly not expected.
 */
export function scheduleWindowNote(student, dayOfWeek) {
  const ds = student?.schedule?.[dayOfWeek];
  if (!ds) return "";
  if (ds.expected === false) return "Not scheduled";
  const start = ds.start || STD_START;
  const end = ds.end || STD_FULL_END;
  const short = (hhmm) => fmtTime(hhmm).replace(":00", "").replace(" AM", "a").replace(" PM", "p");
  const late = start > STD_START;
  const early = end < STD_FULL_END;
  if (late && early) return `${short(start)}–${short(end)}`;
  if (late) return `Arrives ${short(start)}`;
  if (early) return `Leaves ${short(end)}`;
  return "";
}
